import apiClient from './base/apiClient'

/**
 * 카카오 로컬 API 응답의 가게 한 건.
 * 백엔드의 카카오 프록시 엔드포인트가 그대로 전달하는 카카오 응답 형식.
 */
export interface KakaoPlace {
  id: string
  place_name: string
  category_name: string
  category_group_code: string
  category_group_name: string
  phone: string
  address_name: string
  road_address_name: string
  /** 경도 (문자열로 옴) */
  x: string
  /** 위도 (문자열로 옴) */
  y: string
  place_url: string
  /** 검색 중심 좌표로부터의 거리(m). 카테고리 검색에만 옴. */
  distance?: string
}

interface KakaoSearchResponse {
  documents: KakaoPlace[]
  count: number
}

export const kakaoApi = {
  /**
   * 좌표 기반 가게 검색 (음식점 + 카페).
   * 사용자가 지도를 클릭한 좌표를 보내면 반경 30m 안의 가게 목록이 거리순으로 돌아온다.
   * 가장 가까운 가게가 documents[0].
   */
  searchByCoordinate: async (
    lat: number,
    lng: number,
    radius: number = 30
  ): Promise<KakaoPlace[]> => {
    try {
      const response = await apiClient.get<KakaoSearchResponse>(
        '/map/kakao-search-by-coordinate',
        {
          params: { lat, lng, radius },
        }
      )
      return response.data.documents || []
    } catch (error) {
      console.error('[kakaoApi] searchByCoordinate failed', error)
      return []
    }
  },

  /**
   * 키워드 검색 (지하철역, 지역명, 가게명 등).
   */
  searchByKeyword: async (
    keyword: string,
    size: number = 15
  ): Promise<KakaoPlace[]> => {
    if (!keyword.trim()) return []
    try {
      const response = await apiClient.get<KakaoSearchResponse>(
        '/map/kakao-search-by-keyword',
        {
          params: { keyword, size },
        }
      )
      return response.data.documents || []
    } catch (error) {
      console.error('[kakaoApi] searchByKeyword failed', error)
      return []
    }
  },

  /**
   * 카카오 가게 정보를 우리 DB에 등록 (또는 기존 가게의 place_id 반환).
   * 사용자가 빈 곳을 클릭해서 발견한 카카오 가게를 우리 시스템 안으로 가져올 때 사용.
   * 같은 kakao_place_id로 이미 등록된 가게가 있으면 그 가게의 id를 반환 (created: false).
   * 새로 등록되면 created: true.
   */
  registerKakaoPlace: async (
    place: KakaoPlace
  ): Promise<{ place_id: number; created: boolean } | null> => {
    try {
      const response = await apiClient.post<{
        place_id: number
        created: boolean
      }>('/map/register-kakao-place', {
        kakao_place_id: place.id,
        place_name: place.place_name,
        category_name: place.category_name,
        address_name: place.address_name,
        road_address_name: place.road_address_name,
        x: place.x,
        y: place.y,
        phone: place.phone,
      })
      return response.data
    } catch (error) {
      console.error('[kakaoApi] registerKakaoPlace failed', error)
      return null
    }
  },
}
